import { MapContainer, TileLayer, Marker, Popup, Circle, GeoJSON, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { cfmt, pfmt } from '../../lib/api';

// Fix default marker icons (Leaflet + Webpack quirk)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom Pine-green square marker for BestNeighbourhoods brand
const brandIcon = L.divIcon({
    className: 'bn-marker',
    html: '<div style="width:16px;height:16px;background:#2F6B47;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.35);"></div>',
    iconSize: [16, 16],
    iconAnchor: [8, 8],
});

// Southern Ontario bounds (approx.)
const SO_BOUNDS = [
    [42.7, -80.7],   // SW: Windsor-ish corner (over-extended for margin)
    [44.5, -78.2],   // NE: Peterborough-ish
];

// Focus-area center default
const DEFAULT_CENTER = [43.5, -79.5];

function FitToMarkers({ items }) {
    const map = useMap();
    useEffect(() => {
        if (!items?.length) return;
        const bounds = L.latLngBounds(items
            .filter((n) => n.coords?.lat && n.coords?.lng)
            .map((n) => [n.coords.lat, n.coords.lng]));
        if (bounds.isValid()) {
            map.fitBounds(bounds, { padding: [40, 40], maxZoom: 12 });
        }
    }, [items, map]);
    return null;
}

export default function MapView({ items = [], height = 520, center = DEFAULT_CENTER, zoom = 8, singlePoint = null, showCatchment = false, boundaries = null }) {
    const points = singlePoint ? [singlePoint] : items;

    // Split boundary features by kind so we can style them differently.
    const boardFeatures = (boundaries?.features || []).filter((f) => f.properties?.layer === 'board');
    const catchmentFeatures = (boundaries?.features || []).filter((f) => f.properties?.layer === 'school' && f.properties?.kind === 'Catchment');
    const walkZoneFeatures = (boundaries?.features || []).filter((f) => f.properties?.layer === 'school' && f.properties?.kind === 'WalkZone');
    const hasBoundaries = boardFeatures.length + catchmentFeatures.length + walkZoneFeatures.length > 0;

    return (
        <div className="border border-neutral-200 overflow-hidden">
            <MapContainer
                center={center}
                zoom={zoom}
                minZoom={7}
                maxBounds={SO_BOUNDS}
                maxBoundsViscosity={0.8}
                scrollWheelZoom={false}
                style={{ height, width: '100%', background: '#F7F7F5' }}
                data-testid="map-view"
            >
                <TileLayer
                    attribution='&copy; <a href="https://openstreetmap.org">OpenStreetMap</a> &copy; <a href="https://carto.com">CARTO</a>'
                    url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
                    subdomains="abcd"
                    maxZoom={19}
                />
                {!singlePoint && <FitToMarkers items={points} />}

                {/* Board polygons — very light fill, thin dashed border */}
                {boardFeatures.map((f, i) => (
                    <GeoJSON
                        key={`board-${i}-${f.properties?.board_slug}`}
                        data={f}
                        style={() => ({
                            color: f.properties?.kind === 'Catholic' ? '#7A5AA1' : '#2F6B47',
                            weight: 1,
                            dashArray: '3 3',
                            fillColor: f.properties?.kind === 'Catholic' ? '#7A5AA1' : '#2F6B47',
                            fillOpacity: 0.03,
                        })}
                        onEachFeature={(feat, layer) => {
                            layer.bindTooltip(`${feat.properties?.board_name} · Board territory`, { direction: 'top', sticky: true });
                        }}
                    />
                ))}

                {/* Per-school catchment polygons */}
                {catchmentFeatures.map((f, i) => (
                    <GeoJSON
                        key={`catchment-${i}-${f.properties?.school_name}`}
                        data={f}
                        style={() => ({
                            color: '#2F6B47',
                            weight: 1.5,
                            fillColor: '#2F6B47',
                            fillOpacity: 0.12,
                        })}
                        onEachFeature={(feat, layer) => {
                            const p = feat.properties || {};
                            layer.bindTooltip(
                                `${p.school_name} · ${p.boundary_type || 'Catchment'}`,
                                { direction: 'top', sticky: true },
                            );
                        }}
                    />
                ))}

                {/* Walk-zone polygons (HDSB) — lighter styling to distinguish */}
                {walkZoneFeatures.map((f, i) => (
                    <GeoJSON
                        key={`walk-${i}-${f.properties?.school_name}`}
                        data={f}
                        style={() => ({
                            color: '#B87A2C',
                            weight: 1,
                            fillColor: '#B87A2C',
                            fillOpacity: 0.08,
                            dashArray: '5 4',
                        })}
                        onEachFeature={(feat, layer) => {
                            layer.bindTooltip(`${feat.properties?.school_name} · Walk-zone`, { direction: 'top', sticky: true });
                        }}
                    />
                ))}

                {/* Reference ring — only if we have zero real per-school polygons */}
                {showCatchment && !catchmentFeatures.length && !walkZoneFeatures.length && points.filter((n) => n.coords?.lat).map((n) => (
                    <Circle
                        key={`catch-${n.slug}`}
                        center={[n.coords.lat, n.coords.lng]}
                        radius={1500}
                        pathOptions={{ color: '#2F6B47', weight: 1, fillColor: '#2F6B47', fillOpacity: 0.06, dashArray: '4 4' }}
                    />
                ))}
                {points.filter((n) => n.coords?.lat && n.coords?.lng).map((n) => (
                    <Marker key={n.slug} position={[n.coords.lat, n.coords.lng]} icon={brandIcon}>
                        <Popup>
                            <div style={{ minWidth: 200 }}>
                                <div style={{ fontSize: 11, letterSpacing: '.14em', textTransform: 'uppercase', color: '#737373' }}>
                                    {n.city} · {n.province}
                                </div>
                                <div style={{ fontFamily: 'Playfair Display, serif', fontSize: 20, marginTop: 4, letterSpacing: '-0.02em' }}>
                                    {n.name}
                                </div>
                                <div style={{ marginTop: 8, fontFamily: 'JetBrains Mono, monospace', fontSize: 13 }}>
                                    {cfmt(n.median_price)} · <span style={{ color: '#2F6B47' }}>+{pfmt(n.forecast_5yr_pct)} 5Y</span>
                                </div>
                                <Link
                                    to={`/neighbourhoods/${n.slug}`}
                                    style={{
                                        display: 'inline-block', marginTop: 10, padding: '6px 12px',
                                        background: '#0A0A0A', color: '#fff', fontSize: 12,
                                        textDecoration: 'none', letterSpacing: '.04em',
                                    }}
                                >
                                    View neighbourhood →
                                </Link>
                            </div>
                        </Popup>
                    </Marker>
                ))}
            </MapContainer>
            {(showCatchment || hasBoundaries) && (
                <div className="border-t border-neutral-200 px-4 py-2 bg-white text-[11px] text-neutral-500 flex flex-wrap items-center gap-x-5 gap-y-1 font-mono-data" data-testid="map-legend">
                    {catchmentFeatures.length > 0 && (
                        <span className="flex items-center gap-2">
                            <span className="inline-block w-3 h-3" style={{ background: 'rgba(13,59,46,0.35)', border: '1.5px solid #2F6B47' }} />
                            School catchment · board data
                        </span>
                    )}
                    {walkZoneFeatures.length > 0 && (
                        <span className="flex items-center gap-2">
                            <span className="inline-block w-3 h-3" style={{ background: 'rgba(184,122,44,0.35)', border: '1px dashed #B87A2C' }} />
                            Walk-zone (HDSB)
                        </span>
                    )}
                    {boardFeatures.length > 0 && (
                        <span className="flex items-center gap-2">
                            <span className="inline-block w-3 h-3" style={{ background: 'rgba(13,59,46,0.08)', border: '1px dashed #2F6B47' }} />
                            Board territory
                        </span>
                    )}
                    {showCatchment && !catchmentFeatures.length && !walkZoneFeatures.length && (
                        <span className="flex items-center gap-2">
                            <span className="inline-block w-3 h-3" style={{ background: 'rgba(13,59,46,0.06)', border: '1px dashed #2F6B47' }} />
                            Reference 1.5 km ring · authoritative per-school catchment not yet published for this board
                        </span>
                    )}
                </div>
            )}
        </div>
    );
}
